import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubsectionService } from '../../../services/subsection.service';

@Component({
  selector: 'viewer-cinema-mode-button',
  templateUrl: './viewer-cinema-mode-button.component.html',
  styleUrl: './viewer-cinema-mode-button.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ViewerCinemaModeButtonComponent implements OnInit, OnDestroy {
  @Input() active: boolean = false;
  @Input() isFullscreen: boolean = false;
  @Output() readonly exitFullscreenRequest = new EventEmitter<void>();

  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('cinemaButton') cinemaButton!: ElementRef<HTMLButtonElement>;
  private tooltipEl: HTMLDivElement | null = null;
  /** True while pointer is over the button (mouseover/mouseout). */
  private isOverButton: boolean = false;

  ngOnInit(): void {
    let prevCinema: boolean | undefined;
    this.subsectionService.uiState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state: unknown) => {
      const cinemaMode: unknown =
        state && typeof state === 'object' && 'cinemaMode' in state
          ? (state as { cinemaMode?: unknown }).cinemaMode
          : undefined;
      const next: boolean = cinemaMode === true;
      if (prevCinema !== undefined && prevCinema !== next) {
        this.hideTooltip();
      }
      prevCinema = next;
      this.active = next;
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  public showTooltip(): void {
    const button: HTMLButtonElement | undefined = this.cinemaButton?.nativeElement;
    if (!button) {
      return;
    }

    const tooltipText: string = this.active ? 'Exit Cinema Mode' : 'Cinema Mode';

    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'viewer-cinema-tooltip';
      this.tooltipEl.style.position = 'fixed';
      this.tooltipEl.style.pointerEvents = 'none';
      this.tooltipEl.style.zIndex = '20000';
      this.tooltipEl.style.visibility = 'hidden';
      document.body.appendChild(this.tooltipEl);
    }

    this.tooltipEl.textContent = tooltipText;
    this.tooltipEl.style.visibility = 'visible';
    this.positionTooltip();
  }

  public hideTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    this.tooltipEl.style.visibility = 'hidden';
  }

  public onPointerEnter(): void {
    this.isOverButton = true;
    this.showTooltip();
  }

  public onPointerLeave(): void {
    this.isOverButton = false;
    this.hideTooltip();
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    const buttonRect: DOMRect = this.cinemaButton.nativeElement.getBoundingClientRect();

    this.tooltipEl.style.left = `${buttonRect.left + buttonRect.width / 2}px`;
    const tooltipHeight: number = this.tooltipEl.getBoundingClientRect().height || 24;
    this.tooltipEl.style.top = `${buttonRect.top - 10 - tooltipHeight}px`;
    this.tooltipEl.style.transform = 'translateX(calc(-50% - 10px))';
  }

  public onClick(): void {
    this.isOverButton = false;
    this.hideTooltip();

    if (!this.active && this.isFullscreen) {
      this.exitFullscreenRequest.emit();
    }

    this.toggleCinemaMode();
  }

  private toggleCinemaMode(): void {
    this.active = !this.active;
    this.subsectionService.updateUIState({
      ...(this.active ? { commentsCollapsed: true } : {}),
      explorerCollapsed: this.active,
      cinemaMode: this.active,
    });

    requestAnimationFrame(() => {
      this.hideTooltip();
      requestAnimationFrame(() => {
        this.hideTooltip();
      });
    });
  }
}
